import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
} from '@reach/accordion';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '@reach/tabs';
import { LoadingText } from '@we-make-websites/ui-lib/src/components/ui/loading/LoadingText';
import clsx from 'clsx';
import { useContext } from 'use-context-selector';
import type { StoreTab } from 'components/productContext/types/storeProduct';
import IconDown from 'icons/direction/chevron/down.svg';
import globalStyles from 'styles/globalStyles';
import { ProductContext } from '../productContext/ProductProvider';
import styles from './ProductContentTabs.module.scss';

type ProductContentTabs = {
  tabs: StoreTab[];
  isLoading: boolean;
};

const isLoadingClass = 'is-loading';

export const ProductContentTabs = () => {
  const productContext = useContext(ProductContext);
  if (productContext === undefined)
    throw new Error('ProductTitle must be used within a ProductContext');

  const { tabs, isLoading } = productContext;

  return <ProductContentTabsWithProps {...{ tabs, isLoading }} />;
};

export const ProductContentTabsWithProps = ({
  tabs = [],
  isLoading = true,
}: ProductContentTabs) => {
  const tabsWithContent = tabs.filter((tab) => tab.content);
  const hasTabsWithContent = Boolean(tabsWithContent.length);

  if (!hasTabsWithContent) return <></>;

  return (
    <div className={styles.productTabs} data-test-id="productContentTabs">
      <Tabs className={styles.productTabs__content}>
        <TabList>
          {isLoading &&
            [...Array(3)].map((_e, index) => {
              return (
                <Tab key={index} className={styles.productTabs__control}>
                  <span className="text-body-1-bold is-loading">
                    <LoadingText />
                  </span>
                </Tab>
              );
            })}
          {!isLoading &&
            hasTabsWithContent &&
            tabsWithContent.map((tab) => {
              return (
                <Tab key={tab.name} className={styles.productTabs__control}>
                  <h3 className="text-body-1-bold">{tab.name}</h3>
                </Tab>
              );
            })}
        </TabList>

        <TabPanels>
          {isLoading &&
            [...Array(3)].map((_e, index) => {
              return (
                <TabPanel key={index}>
                  <p
                    className={clsx(
                      styles.productTabs__body,
                      'text-heading-h4-mobile text-heading-h4-desktop is-loading'
                    )}
                  >
                    <LoadingText />
                  </p>
                </TabPanel>
              );
            })}
          {!isLoading &&
            hasTabsWithContent &&
            tabsWithContent.map((tab) => {
              return (
                <TabPanel key={tab.name}>
                  <p
                    className={clsx(
                      styles.productTabs__body,
                      'text-heading-h4-mobile text-heading-h4-desktop'
                    )}
                  >
                    {tab.content}
                  </p>
                </TabPanel>
              );
            })}
        </TabPanels>
      </Tabs>

      <Accordion
        defaultIndex={[0]}
        collapsible
        className={styles['productTabs__content--accordion']}
      >
        {isLoading &&
          [...Array(3)].map((_e, i) => {
            return <LoadingTab key={i} animationDelay={i + 1} />;
          })}

        {!isLoading &&
          hasTabsWithContent &&
          tabsWithContent.map((tab) => {
            return <AccordionItemWrapper key={tab.name} {...tab} />;
          })}
      </Accordion>
    </div>
  );
};

const LoadingTab = ({ animationDelay }: { animationDelay: number }) => {
  return (
    <AccordionItem>
      <h3 className={'text-body-subheading'}>
        <AccordionButton>
          <span
            className={isLoadingClass}
            style={{ animationDelay: `${(animationDelay + 1) * 0.15}s` }}
          >
            <LoadingText />
          </span>
          <IconDown
            className={isLoadingClass}
            style={{ animationDelay: `${(animationDelay + 2) * 0.15}s` }}
          />
        </AccordionButton>
      </h3>
      <AccordionPanel>
        <div
          className={[
            globalStyles.isLoading,
            styles.productTabs__loadingPanel,
          ].join(' ')}
          style={{ animationDelay: `${(animationDelay + 3) * 0.15}s` }}
        />
      </AccordionPanel>
    </AccordionItem>
  );
};

const AccordionItemWrapper = ({ name = '', content = '' }) => {
  return (
    <AccordionItem>
      <h3 className={'text-body-subheading'} data-test-id="tab-title">
        <AccordionButton>
          <span className="text-body-1-bold-mobile">{name}</span>
          <IconDown />
        </AccordionButton>
      </h3>
      <AccordionPanel>
        <p
          className="text-heading-h4-mobile text-heading-h4-desktop"
          data-test-id="tabContent"
        >
          {content}
        </p>
      </AccordionPanel>
    </AccordionItem>
  );
};
